import type { AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import * as Sentry from '@sentry/vue'
import { supabase } from '@/features/platform/supabase/supabase.client'
import { router } from '@/router'
import { helpers } from '@/utils/helpers'
import { i18n } from '@/locales'
import { parseDynamicKeys } from './helpers'

Promise.pending = Promise.race.bind(Promise, [])

/**
 * Request interceptor - adds auth token to all requests
 */
export const requestInterceptor = async (config: InternalAxiosRequestConfig) => {
  // Get current session directly from Supabase (not through authService to avoid circular dep)
  const {
    data: { session }
  } = await supabase.auth.getSession()

  // Add authorization header if we have a session
  if (session?.access_token) {
    config.headers.set('Authorization', `Bearer ${session.access_token}`)
  }

  if (config.url) {
    config.url = parseDynamicKeys(config.url, config.dynamicKeys as TIndexedObject | undefined)
  }

  return config
}

/**
 * Request error interceptor
 */
export const requestErrorInterceptor = (error: AxiosError) => {
  return Promise.reject(error)
}

/**
 * Response interceptor - returns data directly
 */
export const responseInterceptor = (response: AxiosResponse) => {
  // Return data directly instead of full axios response
  return response.data
}

/**
 * Response error interceptor - handles API errors
 */
export const responseErrorInterceptor = async (error: AxiosError) => {
  const t = i18n.global.t

  // Supabase handles token refresh automatically
  if (error.code === 'ERR_CANCELED') {
    // Return custom Promise.pending() method so canceled request never resolves
    return Promise.pending()
  }

  switch (error.response?.status) {
    case 401: {
      const authStore = useAuthStore()
      authStore.logOut({ skipBackendCleanup: true }).catch((err) => {
        console.error('Failed to clear session on 401', err)
      })
      helpers.notificationHandler(t('notifications.unauthorized'), 'error')
      router.push({ name: routeNames.authLogin })
      return Promise.pending()
    }
    case 403: {
      helpers.notificationHandler(t('notifications.forbidden'), 'warning')
      router.push({ name: 'Root' })
      return Promise.pending()
    }
    default:
      Sentry.captureException(error)

      if (error.response && error.response.status >= 500) {
        router.push({ name: routeNames.error })
        return Promise.pending()
      }

      return Promise.reject(error)
  }
}
